/**
 * CustomProduct
 *
 * Handles live customization of Shopify product pages:
 * - Manages material/protector/gel/logo selectors
 * - Dynamically overlays customization images on the product media gallery
 * - Updates variant price by mapping selected options to the correct variant
 * - Synchronises line-item properties for cart submission
 *
 * Dependencies: none (vanilla JS + native fetch)
 * Compatibility: modern evergreen browsers (uses MutationObserver, optional chaining, fetch)
 */

(function customProduct() {
	'use strict';

	// ─── Constants ────────────────────────────────────────────────────────────

	const SELECTORS = {
		productJson: '[data-product-json]',
		variantSelects: '.product__info-container variant-selects',
		select: '.product__info-container variant-selects select',
		productFormInput: '.product__info-container variant-selects .product-form__input',
		idInput: '.product__info-container product-form [name="id"]',
		materialSelect: '.material-select',
		logoSelect: '.logo-select',
		protectorSelect: '#ViewProtectorSelect',
		gelSelect: '#GelSelect',
		setLogo: '#SetLogo',
		customizationViews: '#CustomizationViews',
		propertyLine: '.property-line',
		propertyGelLine: '.property-gel-line',
		propertyLogoLine: '.property-logo-line',
		viewsWrap: '#ViewsWrap',
		cartLines: '.item_property',
	};

	const CSS = {
		viewsHidden: 'views-hidden',
		selectorHidden: 'selector-hidden',
		customizationView: 'customization-view',
		customizationNew: 'customization-view-new',
		customizationDone: 'customization-view-xxx',
	};

	// ─── Bootstrap ────────────────────────────────────────────────────────────

	const productJsonEl = document.querySelector(SELECTORS.productJson);
	if (!productJsonEl) {
		console.warn('[CustomProduct] Missing [data-product-json] element — aborting.');
		return;
	}

	let productData;
	try {
		productData = JSON.parse(productJsonEl.innerHTML);
	} catch (err) {
		console.error('[CustomProduct] Failed to parse product JSON:', err);
		return;
	}

	const { variants } = productData;

	const variantSelectsEl = document.querySelector(SELECTORS.variantSelects);
	if (!variantSelectsEl) {
		console.error('[CustomProduct] No <variant-selects> element found.');
		return;
	}

	// ─── Initialisation ───────────────────────────────────────────────────────

	initCustomProduct();

	function initCustomProduct() {
		liveCustomization();
		hideOverlayThumbs();
		triggerInitialSelectChange();
		observeVariantSelects();
		bindEvents();
		syncPropertyLines();
		syncImages();

		if (getVariantSelectionState().isCustom) {
			updateVariantPrice();
		}
	}

	// ─── Event Binding ────────────────────────────────────────────────────────

	function bindEvents() {
		document.addEventListener('optionValueSelectionChange', liveCustomization);

    const materialSelectEls = Array.from(document.querySelectorAll(SELECTORS.materialSelect));
    const logoSelectEl = document.querySelector(SELECTORS.logoSelect);
		const protectorEl = document.querySelector(SELECTORS.protectorSelect);
		const gelEl = document.querySelector(SELECTORS.gelSelect);
		const setLogoEl = document.querySelector(SELECTORS.setLogo);

    materialSelectEls.forEach(el => el.addEventListener('change', onMaterialChange))
    if (logoSelectEl) logoSelectEl.addEventListener('change', onLogoChange)

		if (protectorEl) protectorEl.addEventListener('change', onProtectorChange);
		if (gelEl) gelEl.addEventListener('change', onGelChange);
		if (setLogoEl) setLogoEl.addEventListener('change', onSetLogoChange);
	}

	// ─── MutationObserver ─────────────────────────────────────────────────────

	/**
	 * Watch for dynamically injected <variant-selects> elements (e.g. after a
	 * quick-buy drawer opens) and re-apply selector visibility rules.
	 */
	function observeVariantSelects() {
		const observer = new MutationObserver((mutations) => {
			for (const mutation of mutations) {
				if (mutation.type !== 'childList') continue;

				for (const node of mutation.addedNodes) {
					if (node.nodeType !== Node.ELEMENT_NODE) continue;

					if (node.matches?.(SELECTORS.variantSelects)) {
						applySelectorVisibility();
					}

					node.querySelectorAll?.(SELECTORS.variantSelects)
						.forEach(() => applySelectorVisibility());
				}
			}
		});

		observer.observe(document.documentElement, {
			childList: true,
			subtree: true
		});
	}

	// ─── Variant Selection State ──────────────────────────────────────────────

	/**
	 * Inspect all <select> elements inside <variant-selects> and determine
	 * whether one of them is currently set to the "Custom" option.
	 *
	 * @returns {{ isCustom: boolean, customIndex: number|undefined }}
	 */
	function getVariantSelectionState() {
		const selects = Array.from(document.querySelectorAll(SELECTORS.select));

		for (let i = 0; i < selects.length; i++) {
			if (normalise(selects[i].value) === 'custom') {
				return {
					isCustom: true,
					customIndex: i
				};
			}
		}

		return {
			isCustom: false,
			customIndex: undefined
		};
	}

	// ─── Live Customization ───────────────────────────────────────────────────

	/**
	 * Central dispatcher called whenever the selected variant option changes.
	 * Shows/hides the customization UI panel and synchronises property lines.
	 */
	function liveCustomization() {
		if (!isCustomProduct(productData)) return;

		const { isCustom } = getVariantSelectionState();

		applySelectorVisibility();

		const viewsEl = document.querySelector(SELECTORS.customizationViews);

		if (isCustom) {
			viewsEl?.classList.remove(CSS.viewsHidden);

			disablePropertyLines(SELECTORS.propertyLine);

			const setLogoEl = document.querySelector(SELECTORS.setLogo);
			setLogoEl?.dispatchEvent(new Event('change'));

			updateVariantPrice();
			syncPropertyLines();
		} else {
			viewsEl?.classList.add(CSS.viewsHidden);

			disablePropertyLines(SELECTORS.propertyLine);
			disablePropertyLines(SELECTORS.propertyLogoLine);
		}
	}

	// ─── Selector Visibility ──────────────────────────────────────────────────

	function applySelectorVisibility() {
		const { isCustom, customIndex } = getVariantSelectionState();
		isCustom ? hideSelectorsExcept(customIndex) : showAllSelectors();
	}

	/**
	 * Hide every product-form input except the one at `keepIndex`.
	 * @param {number} keepIndex
	 */
	function hideSelectorsExcept(keepIndex) {
		document.querySelector(SELECTORS.customizationViews).classList.remove(CSS.selectorHidden);
		const selects = Array.from(document.querySelectorAll(SELECTORS.select));
		selects.forEach((sel, i) => {
			const wrapper = sel.closest(SELECTORS.productFormInput);
			wrapper?.classList.toggle(CSS.selectorHidden, i !== keepIndex);
		});
	}

	function showAllSelectors() {
		document.querySelector(SELECTORS.customizationViews).classList.add(CSS.selectorHidden);
		document.querySelectorAll(SELECTORS.select).forEach((sel) => {
			sel.closest(SELECTORS.productFormInput)?.classList.remove(CSS.selectorHidden);
		});
	}

	// ─── Event Handlers ───────────────────────────────────────────────────────

	function onMaterialChange() {
		const type = this.getAttribute('data-type');

		// Selecting a front material deactivates the protector select
		if (type === 'front' && this.value !== 'unselected') {
			const protectorEl = document.querySelector(SELECTORS.protectorSelect);
			if (protectorEl) {
				protectorEl.value = 'unselected';
				protectorEl.dispatchEvent(new Event('change'));
			}
		}

		syncPropertyLines();
		syncImages();
		updateVariantPrice();
	}

	function onProtectorChange() {
    const { customSlide } = getActiveSelection();
		const dataView = this.getAttribute('data-view');

		if (this.value !== 'unselected') {
			// Deactivate front material when a protector is selected
			const frontMaterial = document.querySelector(`${SELECTORS.materialSelect}[data-type="front"]`);
			if (frontMaterial) {
				frontMaterial.value = 'unselected';
				frontMaterial.dispatchEvent(new Event('change'));
			}
		}

		const protectorKey = `protector-${this.value}`;
		const matchingImage = productData.images?.find((src) => src.includes(protectorKey)) ?? null;

		appendImages([{
      slide: customSlide,
			src: matchingImage,
			index: dataView
		}]);

		syncPropertyLines();
		updateVariantPrice();
	}

	function onGelChange() {
		syncPropertyLines();
		updateVariantPrice();
	}

	function onLogoChange() {
		const { customSlide } = getActiveSelection();
		const src = this.checked ? this.getAttribute('data-url') : null;
		const view = this.getAttribute('data-view');

		appendImages([{
			slide: customSlide,
			src,
			index: view
		}]);
	}

	function onSetLogoChange() {
		const logoLineEl = document.querySelector(SELECTORS.propertyLogoLine);
		if (!logoLineEl) return;

		const checked = document.querySelector(SELECTORS.setLogo)?.checked ?? false;

		logoLineEl.value = checked ? 'yes' : 'no';
		logoLineEl.disabled = false;
	}

	// ─── Property Lines ───────────────────────────────────────────────────────

	/**
	 * Synchronise all hidden property-line inputs with the currently selected
	 * customization options so they are submitted with the cart form.
	 */
	function syncPropertyLines() {
		const { isCustom } = getVariantSelectionState();
		const selects = Array.from(
			document.querySelectorAll(`${SELECTORS.materialSelect}, ${SELECTORS.protectorSelect}`)
		);

		selects.forEach((selectEl) => {
			const selected = selectEl.options[selectEl.selectedIndex];
			const dataView = selected?.getAttribute('data-view');
			const dataType = selected?.getAttribute('data-type');
			const dataUrl = selected?.getAttribute('data-url');
			const value = selected?.value ?? '';
			const lineEl = dataView ? document.getElementById(`${dataView}-line`) : null;

			if (!lineEl) return;

			if (dataType === 'unselected' || !isCustom) {
				lineEl.value = value;
				lineEl.disabled = true;
			} else {
				lineEl.value = value.replace(/_/g, ' ');
				lineEl.disabled = false;
			}
		});

		// Gel line
		const gelLineEl = document.querySelector(SELECTORS.propertyGelLine);
		const gelEl = document.querySelector(SELECTORS.gelSelect);
		if (gelLineEl && gelEl) {
			const checked = gelEl.checked;
			gelLineEl.value = checked ? 'yes' : 'no';
			gelLineEl.disabled = !checked;

			if (!isCustom) {
				gelLineEl.disabled = true;
			}
		}

		// Logo line
		const logoLineEl = document.querySelector(SELECTORS.propertyLogoLine);
		const setLogoEl = document.querySelector(SELECTORS.setLogo);
		if (logoLineEl && setLogoEl) {
			logoLineEl.value = setLogoEl.checked ? 'yes' : 'no';
			logoLineEl.disabled = false;

			if (!isCustom) {
				logoLineEl.disabled = true
			}
		}
	}

	function disablePropertyLines(selector) {
		document.querySelectorAll(selector).forEach((el) => {
			el.disabled = true;
		});
	}

	// ─── Image Management ─────────────────────────────────────────────────────

	/**
	 * Remove gallery thumbnail slides whose source URL is listed as an overlay
	 * image (via `data-url-original` on option elements). These are decorative
	 * overlays, not standalone product images.
	 */
	function hideOverlayThumbs() {
		const overlayUrls = Array.from(document.querySelectorAll('option[data-url-original]'))
			.map((o) => o.getAttribute('data-url-original'))
			.filter(Boolean);

		overlayUrls.forEach((url) => {
			document.querySelectorAll(`media-gallery [srcset*="${url}"]`).forEach((imgEl) => {
				imgEl.closest('.slider__slide')?.remove();
			});
		});
	}

	/**
	 * Read all `.material-select` values and overlay the corresponding images
	 * onto the active custom slide.
	 */
	function syncImages() {
		const { customSlide } = getActiveSelection();

		const images = Array.from(document.querySelectorAll(SELECTORS.materialSelect))
			.map((selectEl, index) => {
				const selected = selectEl.options[selectEl.selectedIndex];
				return {
					src: selected?.getAttribute('data-url') ?? null,
					index,
					slide: customSlide,
				};
			});

		appendImages(images);
	}

	/**
	 * Append (or remove) overlay `<img>` elements onto a slide.
	 *
	 * - If `src` is truthy, a new image is created and faded in.
	 * - If `src` is falsy, any existing image with the matching `data-view` is
	 *   faded out and removed after the CSS transition ends.
	 *
	 * @param {Array<{ src: string|null, index: number|string, slide?: Element }>} images
	 */
	function appendImages(images) {
		images.forEach(({ src, index, slide }) => {
			if (src) {
				const img = document.createElement('img');
				img.src = src;
				img.className = `${CSS.customizationView} ${CSS.customizationNew}`;
				img.dataset.view = index;

				img.addEventListener('load', function onLoad() {
					this.className = CSS.customizationView;
					this.removeEventListener('load', onLoad);
				});

				img.addEventListener('transitionend', function onTransition() {
					const sameView = document.querySelectorAll(`img[data-view="${index}"]`);
					if (sameView.length > 1) {
						this.className = `${CSS.customizationView} ${CSS.customizationDone}`;
						sameView.forEach((el) => {
							if (!el.classList.contains(CSS.customizationDone)) el.remove();
						});
						this.className = CSS.customizationView;
					}
					this.removeEventListener('transitionend', onTransition);
				});

				slide?.append(img);

			} else {
				// Fade out and remove existing overlay
				document.querySelectorAll(`img[data-view="${index}"]`).forEach((img) => {
					img.addEventListener('transitionend', function onTransition() {
						this.remove();
						this.removeEventListener('transitionend', onTransition);
					});
					img.style.opacity = '0';
				});
			}
		});
	}

	// ─── Variant Price Sync ───────────────────────────────────────────────────

	/**
	 * Resolve the correct custom variant based on the current option selections,
	 * then update the hidden `[name="id"]` input so Shopify reflects the right
	 * price, availability, and add-to-cart state.
	 */
	function updateVariantPrice() {
		const idInputEl = document.querySelector(SELECTORS.idInput);
		if (!idInputEl) return;

		const selectedTypes = getSelectedTypes();
		const customVariantId = resolveCustomVariantId(selectedTypes, variants);
		const currentVariantId = parseInt(idInputEl.value, 10);

		if (customVariantId && customVariantId !== currentVariantId) {
			const selects = document.querySelectorAll(SELECTORS.variantSelects);
			const sortedSelectedTypes = JSON.parse(JSON.stringify(selectedTypes)).sort();
			selects.forEach(select => {
				const options = Array.from(select.querySelectorAll('option'));
				options.forEach(option => {
					const optionCleanValueArr = option.value.toLowerCase().split('+').map(str => str.trim()).map(str => str.replaceAll(' ', '_')).sort();
					const isMatch = optionCleanValueArr.length === sortedSelectedTypes.length && optionCleanValueArr.every((val, index) => val === sortedSelectedTypes[index]);
					
					if (isMatch) {
						select.value = option.value;
						option.setAttribute('selected', 'selected');
					}
				})
			})

			idInputEl.value = customVariantId;
			document.querySelector(SELECTORS.variantSelects).dispatchEvent(new Event('change'));
		}
	}

	// ─── Selection Helpers ────────────────────────────────────────────────────

	/**
	 * Return the currently-selected type strings (e.g. ['leather', 'carbon'])
	 * used to match against variant option arrays.
	 *
	 * Falls back to the first available material option if nothing is selected.
	 *
	 * @returns {string[]}
	 */
	function getSelectedTypes() {
		const selects = Array.from(
			document.querySelectorAll(`${SELECTORS.materialSelect}, .protector-select`)
		);

		let selectedTypes = selects.reduce((acc, selectEl) => {
			const selected = selectEl.options[selectEl.selectedIndex];
			const type = selected?.getAttribute('data-type');
			if (type && type !== 'unselected') acc.push(type);
			return acc;
		}, []);

		// Auto-select the first material if nothing was chosen yet
		if (!selectedTypes.length) {
			const firstSelect = selects[0];
			if (firstSelect) {
				const firstOption = firstSelect.options[1]; // skip placeholder at index 0
				if (firstOption) {
					firstSelect.value = firstOption.value;
					firstSelect.dispatchEvent(new Event('change'));
					const type = firstOption.getAttribute('data-type');
					if (type) selectedTypes.push(type);
				}
			}
		}

		const gelEl = document.querySelector(SELECTORS.gelSelect);
		if (gelEl?.checked) selectedTypes.push('gel');

		return selectedTypes;
	}

	/**
	 * Resolve the product media slide and variant objects for the current
	 * "custom" selection.
	 *
	 * @returns {{ customVariant: object, selectedVariant: object, customSlide: Element }}
	 */
	function getActiveSelection() {
		const idInputEl = document.querySelector(SELECTORS.idInput);
		const selectedTypes = getSelectedTypes();
		const customVariantId = resolveCustomVariantId(selectedTypes, variants);
		const selectedVariantId = idInputEl ? parseInt(idInputEl.value, 10) : NaN;

		const customVariant = variants.find((v) => v.id === customVariantId);
		const selectedVariant = variants.find((v) => v.id === selectedVariantId);

		if (!customVariant?.featured_image?.src) {
			throw new Error('[CustomProduct] Custom variant has no featured image.');
		}

		const imgSrc = customVariant.featured_image.src;
		const galleryEl = document.querySelector(`media-gallery [srcset*="${imgSrc}"]`);
		const customSlide = galleryEl?.closest('.product__media-item') ?? null;

		return {
			customVariant,
			selectedVariant,
			customSlide
		};
	}

	// ─── Variant Matching ─────────────────────────────────────────────────────

	/**
	 * Find the variant whose title (split on `/` or `+`) contains "custom" plus
	 * exactly the provided `selectedTypes` — order-independent.
	 *
	 * @param {string[]} selectedTypes
	 * @param {object[]} variantList
	 * @returns {number|undefined} variant.id or undefined when not found
	 */
	function resolveCustomVariantId(selectedTypes, variantList) {
		for (const variant of variantList) {
			const parts = variant.title
				.replace(/\+/g, '/')
				.toLowerCase()
				.split('/')
				.map((p) => p.trim().replace(/\s+/g, '_'));

			const customIdx = parts.indexOf('custom');
			if (customIdx === -1) continue;

			const remaining = parts.filter((_, i) => i !== customIdx);

			const same =
				remaining.length === selectedTypes.length &&
				remaining.every((p) => selectedTypes.includes(p)) &&
				selectedTypes.every((t) => remaining.includes(t));

			if (same) return variant.id;
		}

		return undefined;
	}

	/**
	 * @param {object} product
	 * @returns {boolean} true when at least one variant option equals "custom"
	 */
	function isCustomProduct(product) {
		return product.variants
			.flatMap((v) => v.options)
			.some((opt) => normalise(opt) === 'custom');
	}

	// ─── Initial Trigger ──────────────────────────────────────────────────────

	/**
	 * When the page loads with a "Custom" option pre-selected, fire a change
	 * event so all dependent UI (images, price, property lines) is initialised.
	 */
	function triggerInitialSelectChange() {
		const {
			isCustom
		} = getVariantSelectionState();

		if (isCustom) {
			window.addEventListener('load', () => {
				const firstSelect = document.querySelector(SELECTORS.select);
				firstSelect?.dispatchEvent(new Event('change', {
					bubbles: true
				}));
			}, {
				once: true
			});
		}
	}

	// ─── Utilities ────────────────────────────────────────────────────────────

	/**
	 * Normalise a string: strip whitespace and lowercase.
	 * @param {string} str
	 * @returns {string}
	 */
	function normalise(str) {
		return str.replace(/\s/g, '').toLowerCase();
	}
})();